'use client'

import { useState, MouseEvent } from 'react'
import { Image, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'
import { last } from 'lodash'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  deletePhotoSuspicion,
  MessagePhotoModel,
  messagingClickEvent,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import SuspiciousPhoto from './SuspiciousPhoto'
import MessageTime from '../MessageTime'
import DeletedMessage from '../DeletedMessage'
import MessageMenu from '../MessageMenu'

type Props = {
  photo: MessagePhotoModel
  isCurrentAuthor: boolean
  isLastAndRead: boolean
  isLast: boolean
  showTime: boolean
  createdTime: string
  messageId: number | string
  onThumbnailClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

const PhotoMessage = ({
  photo,
  isCurrentAuthor,
  isLastAndRead,
  isLast,
  showTime,
  createdTime,
  messageId,
  onThumbnailClick,
}: Props) => {
  const translate = useTranslate('conversation.message')
  const { track } = useTracking()
  const { conversation } = useConversationContext()
  const oppositeUser = conversation?.oppositeUser

  const [isSuspicious, setIsSuspicious] = useState(photo.isSuspicious)

  const { url, thumbnails } = photo
  const thumbnail = last(thumbnails)
  const imageUrl = thumbnail?.url || url

  const removeSuspiciousOverlay = async () => {
    const response = await deletePhotoSuspicion(photo.id)

    if ('errors' in response) return

    setIsSuspicious(false)
  }

  const getMessageContainerAriaLabel = () => {
    if (photo.isHidden) {
      return isCurrentAuthor
        ? translate('a11y.deleted_message_from_me')
        : translate('a11y.deleted_message_from_member', { member: oppositeUser?.login })
    }

    return isCurrentAuthor
      ? translate('a11y.photo_message_from_me')
      : translate('a11y.photo_message_from_member', {
          member: oppositeUser?.login,
        })
  }

  const renderSuspiciousOverlay = () => {
    if (!isSuspicious) return null

    return <SuspiciousPhoto isCurrentAuthor={isCurrentAuthor} onClick={removeSuspiciousOverlay} />
  }

  const renderStatus = () => {
    if (!isCurrentAuthor || !isLastAndRead) return null

    return (
      <>
        <Spacer size="small" />
        <div className="u-text-right">
          <Text
            as="h4"
            text={translate('is_read')}
            theme="muted"
            type="caption"
            alignment="right"
          />
        </div>
      </>
    )
  }

  const renderMessageTime = () => {
    if (!showTime) return null

    return <MessageTime time={createdTime} />
  }

  const renderMessagePhoto = () => {
    return <Image testId="thumbnail" src={imageUrl} size="x2-large" styling="rounded" />
  }

  const handlePhotoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    track(
      messagingClickEvent({
        target: 'open_photo',
        conversationId: conversation?.id,
        userSide: conversation?.transaction?.userSide,
        screen: 'conversation',
      }),
    )

    onThumbnailClick?.(event)
  }

  const renderMessageContent = () => {
    const messageContainerAriaLabel = getMessageContainerAriaLabel()

    if (photo.isHidden) {
      return (
        <DeletedMessage
          inverse={isCurrentAuthor}
          aria={{ 'aria-label': messageContainerAriaLabel }}
        />
      )
    }

    return (
      <a
        href={url}
        // We need rel="fancybox" attribute until we migrate to using <Modal /> overlay
        // eslint-disable-next-line react/no-invalid-html-attribute
        rel="fancybox"
        aria-label={messageContainerAriaLabel}
        data-testid="photo-message-link"
        onClick={handlePhotoClick}
      >
        {renderMessagePhoto()}
      </a>
    )
  }

  const renderMenu = () => {
    if (photo.isHidden) return null

    return (
      <MessageMenu
        messageId={messageId}
        photoId={photo.id}
        isLast={isLast}
        isCurrentAuthor={isCurrentAuthor}
      />
    )
  }

  const photoMessageClass = classNames(
    'u-position-relative',
    'conversation-message-container',
    'conversation-message-container__grouped',
    {
      'conversation-message-container__current-author': isCurrentAuthor,
    },
  )

  const messageMenuContainerClass = classNames('conversation-message-menu-container', {
    'conversation-message-menu-container--reverse': !isCurrentAuthor,
  })

  return (
    <div data-testid="photo-message">
      {renderMessageTime()}
      <div className={photoMessageClass}>
        <div className={messageMenuContainerClass}>
          {renderMenu()}
          <div>
            {renderMessageContent()}
            {renderSuspiciousOverlay()}
            {renderStatus()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PhotoMessage
